/**
 * Session state for React components.
 *
 * - `useSession()`: the signed-in wallet, or `null`. Fetched once and shared by every component.
 * - `useSignIn()`: connects a wallet and runs the sign-in sequence.
 * - `useSignOut()`: ends the session and disconnects the wallet.
 *
 * The session (a cookie the API sets) is the source of truth for who is signed in. The wallet
 * connection is only needed to sign. A session stays valid while the wallet is locked or closed.
 *
 * The API decides when a session ends. It is checked again whenever the page comes back into
 * view, and any request the API refuses for want of a session ends it on screen too. Whenever the
 * signed-in wallet changes, however it changed, everything fetched for the previous wallet is
 * dropped and fetched again for the new one, so a page never shows one wallet's accounts,
 * submissions or requests to another.
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import type { Address } from "viem";
import { type Connector, useConnect, useConnection, useDisconnect, useSignMessage } from "wagmi";

import { ApiRequestError, api } from "../api/client.js";
import { useAppConfig } from "../config.js";
import { type Session, signIn } from "./sign-in.js";

/** Query key of the session in the React Query cache. */
export const SESSION_QUERY_KEY = ["session"] as const;

/** Queries whose data is the same for every visitor. All others belong to the signed-in wallet. */
const PUBLIC_QUERIES = new Set(["session", "campaigns", "campaign-result", "deployments"]);

/**
 * Drops the signed-in wallet's own data (accounts, submissions, requests, payouts, admin lists):
 * pages that show it fetch it again, for whoever is signed in now.
 */
export function resetWalletData(queryClient: QueryClient): Promise<void> {
  return queryClient.resetQueries({
    predicate: (query) => !PUBLIC_QUERIES.has(String(query.queryKey[0])),
  });
}

/**
 * Resets the wallet's own data whenever the signed-in wallet changes: sign-in, sign-out, or a
 * session the API ended. Used once, by the page frame.
 */
export function useWalletDataReset() {
  const session = useSession();
  const queryClient = useQueryClient();
  // Unknown until the session is first read. That first answer resets nothing.
  const wallet = session.isSuccess ? (session.data?.wallet ?? null) : undefined;
  const shown = useRef(wallet);

  useEffect(() => {
    if (wallet === undefined) {
      return;
    }
    if (shown.current !== undefined && shown.current !== wallet) {
      void resetWalletData(queryClient);
    }
    shown.current = wallet;
  }, [wallet, queryClient]);
}

/**
 * Ends the session on screen when the API says it has ended: it expired, or the wallet signed
 * out in another tab. Any request may find out. The header then offers "Sign in" again.
 */
export function endEndedSession(queryClient: QueryClient, error: unknown): void {
  if (
    error instanceof ApiRequestError &&
    error.status === 401 &&
    queryClient.getQueryData<Session | null>(SESSION_QUERY_KEY)
  ) {
    queryClient.setQueryData(SESSION_QUERY_KEY, null);
  }
}

/** The signed-in wallet: `data` is the session, or `null` when nobody is signed in. */
export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async (): Promise<Session | null> => {
      try {
        return await api.get<Session>("/api/auth/session");
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    // Checked again when the page comes back into view after a minute or more. The session may
    // have expired, or the wallet signed out in another tab, meanwhile.
    staleTime: 60_000,
  });
}

/**
 * Signing in with a chosen wallet. `mutate(connector)` connects the wallet (unless it is already
 * connected), asks it to sign the sign-in message and stores the new session.
 */
export function useSignIn() {
  const { chainId } = useAppConfig();
  const queryClient = useQueryClient();
  const connection = useConnection();
  const { mutateAsync: connect } = useConnect();
  const { mutateAsync: signMessage } = useSignMessage();

  return useMutation({
    mutationFn: async (connector: Connector): Promise<Session> => {
      let address: Address | undefined;
      if (connection.isConnected && connection.connector?.uid === connector.uid) {
        address = connection.address;
      } else {
        const result = await connect({ connector, chainId });
        address = result.accounts[0];
      }
      if (!address) {
        throw new Error("The wallet did not provide an account.");
      }
      const account = address;
      return signIn({
        address: account,
        chainId,
        origin: window.location.origin,
        signMessage: (message) => signMessage({ message, account, connector }),
      });
    },
    onSuccess: (session) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, session);
    },
  });
}

/** Signing out: ends the session on the API and disconnects the wallet. */
export function useSignOut() {
  const queryClient = useQueryClient();
  const { mutate: disconnect } = useDisconnect();

  return useMutation({
    mutationFn: () => api.post<void>("/api/auth/sign-out"),
    // Cleared locally even if the request fails, so the user is never stuck signed in on screen.
    onSettled: () => {
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
      disconnect();
    },
  });
}

/**
 * Ends the session when the connected wallet switches to another account, so the app never acts
 * for one wallet while the wallet itself shows another. A wallet that is merely locked or
 * disconnected leaves the session open.
 */
export function useEndSessionOnAccountSwitch() {
  const { data: session } = useSession();
  const { address } = useConnection();
  const { mutate: endSession } = useSignOut();

  useEffect(() => {
    if (session && address && address.toLowerCase() !== session.wallet) {
      endSession();
    }
  }, [session, address, endSession]);
}
